import { type HealthArea, SCHEMA_NAMES } from "instantdb/types";
import { MAX_FREE_HEALTH_AREAS } from "@/config/constants";
import { useResourceAtoms } from "@/hooks/resources/useResourceAtoms";
import { getHealthAreas } from "instantdb/client/healthArea";
import { useUser } from "@/hooks/useUser";
import { useResourceOrder } from "@/hooks/resources/useResourceOrder";

type UseHealthAreasReturn = {
  healthAreas: HealthArea[];
  handleCreateHealthArea: ({ dateTime }: { dateTime?: Date }) => void;
  handleCopyHealthArea: (id: Omit<HealthArea, "id">) => void;
  handleEditHealthArea: (healthArea: HealthArea) => void;
  totalHealthAreas: number;
  goalsInHealthAreas: number;
  hasFreeHealthAreas: boolean;
  isLoading: boolean;
};

export const useHealthAreas = (): UseHealthAreasReturn => {
  const { hasPro, userId } = useUser();

  // Get healthAreas with ordering applied (now in the getHealthAreas function)
  const { healthAreas, isLoading } = getHealthAreas(userId);

  const { orderedResources: orderedHealthAreas, isLoading: isOrderingLoading } =
    useResourceOrder<HealthArea>({
      slug: SCHEMA_NAMES.healthAreas,
      resources: healthAreas,
    });

  const goalsInHealthAreas = orderedHealthAreas.reduce(
    (prev, cur) => prev + (Array.isArray(cur?.goals) ? (cur?.goals?.length ?? 0) : 0),
    0,
  );

  const hasFreeHealthAreas = healthAreas.length < MAX_FREE_HEALTH_AREAS || hasPro;

  const { handleCreateResource, handleCopyResource, handleEditResource } = useResourceAtoms({
    slug: SCHEMA_NAMES.healthAreas,
    canCreate: hasFreeHealthAreas,
  });

  return {
    healthAreas: orderedHealthAreas,
    handleCreateHealthArea: handleCreateResource,
    handleCopyHealthArea: handleCopyResource,
    handleEditHealthArea: handleEditResource,
    totalHealthAreas: healthAreas.length,
    goalsInHealthAreas,
    hasFreeHealthAreas,
    isLoading: isLoading || isOrderingLoading,
  };
};
